import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Tag, Plus, MapPin, Loader2, ImageOff, Clock } from "lucide-react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { timeAgo } from "@/lib/format";

const PAGE = 12;

function ListingCard({ item }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const resolve = (src) => (src && src.startsWith("/") ? `${backend}${src}` : src);
  const cover = (item.images || [])[0];
  return (
    <Link
      to={`/obyava/${item.slug}`}
      data-testid={`listing-card-${item.slug}`}
      className="group overflow-hidden rounded-xl border border-slate-200 bg-white transition-shadow hover:shadow-md"
    >
      <div className="relative aspect-video bg-slate-100">
        {cover ? (
          <img src={resolve(cover)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-300">
            <ImageOff className="h-8 w-8" />
            <span className="text-xs font-medium text-slate-400">Без снимка</span>
          </div>
        )}
        <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-bold text-white">
          <Tag className="h-3 w-3" />
          {item.listing_type}
        </span>
      </div>
      <div className="p-4">
        <h3 className="line-clamp-2 font-display text-base font-bold text-slate-900 group-hover:text-primary">
          {item.title}
        </h3>
        {item.price && (
          <div className="mt-1.5 font-display text-lg font-extrabold text-primary">{item.price}</div>
        )}
        <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{item.location}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{timeAgo(item.published_at)}</span>
        </div>
      </div>
    </Link>
  );
}

export default function ListingsPage() {
  const [type, setType] = useState("all");

  const { data: types } = useQuery({
    queryKey: ["listing-types"],
    queryFn: () => api.get("/listing-types").then((r) => r.data),
  });

  const {
    data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading,
  } = useInfiniteQuery({
    queryKey: ["listings", type],
    queryFn: ({ pageParam = 0 }) =>
      api.get(`/listings`, { params: { type, skip: pageParam, limit: PAGE } }).then((r) => r.data),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    initialPageParam: 0,
  });

  const items = (data?.pages || []).flatMap((p) => p.items);
  const total = data?.pages?.[0]?.total ?? 0;

  const chips = [{ name: "Всички", value: "all" }, ...(types || []).map((t) => ({ name: t.name, value: t.name }))];

  return (
    <Layout>
      <Seo
        title="Обяви"
        description="Безплатни обяви за квартал Подуяне — продажби, услуги, имоти, автомобили, работа и още."
      />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">Обяви</h1>
            <p className="mt-2 text-slate-600">Безплатни обяви от жителите на Подуяне.</p>
          </div>
          <Link
            to="/publikuvai-obyava"
            data-testid="submit-listing-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#0041A8]"
          >
            <Plus className="h-4 w-4" />
            Публикувай обява
          </Link>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="listing-filters">
          {chips.map((c) => (
            <button
              key={c.value}
              onClick={() => setType(c.value)}
              data-testid={`listing-filter-${c.value}`}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                type === c.value ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-xl border border-dashed border-slate-200 bg-white p-16 text-center text-slate-500" data-testid="listings-empty">
            <Tag className="h-9 w-9 text-slate-300" />
            <p>Все още няма обяви в тази категория.</p>
            <Link to="/publikuvai-obyava" className="mt-1 font-semibold text-primary hover:underline">
              Бъдете първи — публикувайте обява
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-6 text-sm text-slate-400">{total} обяви</div>
            <div className="mt-3 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="listings-grid">
              {items.map((item) => (<ListingCard key={item.id} item={item} />))}
            </div>
            {hasNextPage && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  data-testid="listings-load-more"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-6 py-2.5 text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary disabled:opacity-60"
                >
                  {isFetchingNextPage && <Loader2 className="h-4 w-4 animate-spin" />}
                  Зареди още обяви
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
